<template>
	<div
		:class="{
			'submit-configuration': true,
			'submit-configuration--partner-area': isPartnerArea,
			'submit-configuration--client-area': !isPartnerArea,
		}"
	>
		<div>
			<div v-if="isDownloadEnabled" class="form-overlay__button">
				<button
					@click.prevent="submitDownload"
					type="button"
					:disabled="!isAbleToDownload"
					class="button"
				>
					Wygeneruj ofertę
				</button>
			</div>
			<h4 v-if="!isPartnerArea" class="submit-configuration__header">Zakończenie konfiguracji:</h4>
			<span v-if="status === 'pending-download'">Pobieranie...</span>
			<div
				v-if="!isPartnerArea"
				:class="{
					'submit-configuration__input': true,
					'submit-configuration__input--error': didTryToSubmit && !isNameValid,
				}"
			>
				<span v-if="didTryToSubmit && !isNameValid">Pole obowiązkowe</span>
				<span v-else></span>
				<input
					style="margin-bottom: 0.5rem"
					type="text"
					v-model="name"
					placeholder="Imię i nazwisko"
				/>
			</div>
			<div
				:class="{
					'submit-configuration__input': true,
					'submit-configuration__input--error': didTryToSubmit && !isEmailValid,
				}"
			>
				<span v-if="didTryToSubmit && !isEmailValid">Podaj poprawny adres email</span>
				<span v-else></span>
				<input
					style="margin-bottom: 0.5rem"
					type="email"
					v-model="email"
					placeholder="Adres e-mail"
				/>
			</div>
			<div
				:class="{
					'submit-configuration__input': true,
					'submit-configuration__input--error': didTryToSubmit && !isPhoneValid,
				}"
			>
				<span v-if="didTryToSubmit && !isPhoneValid">Podaj poprawny numer telefonu</span>
				<span v-else></span>
				<input
					type="tel"
					v-model="phone"
					:placeholder="'Numer telefonu' + (isPartnerArea ? ' (opcjonalnie)' : '')"
				/>
			</div>
			<ul v-if="!isPartnerArea" class="consents">
				<li :class="{consents__item: true, 'consents__item--error': didTryToSubmit && !consent1}">
					<label @click="consent1 = !consent1" class="consents__label">
						<span :class="{consents__checkmark: true, '--checked': consent1}"></span>Oświadczam, że
						akceptuję Politykę prywatności Meditrans Sp. z o.o. sp.k. oraz politykę cookies serwisu:
						<a target="_blank" href="https://chirana.pl/polityka-prywatnosci/">www.chirana.pl</a
						>.</label
					>
				</li>
				<li :class="{consents__item: true, 'consents__item--error': didTryToSubmit && !consent2}">
					<label @click="consent2 = !consent2" class="consents__label">
						<span :class="{consents__checkmark: true, '--checked': consent2}"></span>
						Wyrażam zgodę na przetwarzanie moich danych osobowych przez Meditrans Sp. z o.o. sp.k. z
						siedzibą w Kielcach dla celów marketingu bezpośredniego, wykonywanego przy użyciu
						telekomunikacyjnych urządzeń końcowych oraz automatycznych systemów wywołujących,
						których jestem użytkownikiem, zgodnie z art. 172 ustawy z dnia 16 lipca 2004 r. Prawo
						telekomunikacyjne (Dz.U. z 2017 r., poz. 1907 ze zm.).
					</label>
				</li>

				<li :class="{consents__item: true, 'consents__item--error': didTryToSubmit && !consent3}">
					<label @click="consent3 = !consent3" class="consents__label">
						<span :class="{consents__checkmark: true, '--checked': consent3}"></span>
						Wyrażam zgodę na przesyłanie informacji handlowych i marketingowych za pomocą środków
						komunikacji elektronicznej w rozumieniu ustawy z dnia 18 lipca 2002 roku o świadczenie
						usług drogą elektroniczną (Dz.U.2017.1219 t.j.) na podany adres e-mail na temat usług,
						towarów oferowanych przez przez Meditrans Sp. z o.o. sp.k. z siedzibą w Kielcach.
					</label>
				</li>
			</ul>
			<button
				v-if="!isPartnerArea || user"
				@click.prevent="submit"
				role="submit"
				class="button prev-next__button"
				:disabled="!isAbleToSend"
			>
				Zakończ i wyślij konfigurację na maila
			</button>
			<span v-if="status === 'pending-send'">Wysyłanie...</span>
			<span
				v-if="message && ['success', 'error'].includes(status)"
				:class="`submit-configuration__${status}`"
			>
				{{ message }}
			</span>
		</div>
	</div>
</template>

<script>
import * as R from 'ramda';
import {formatPrice} from '../../common/helpers/format-price';
import {api} from '@/core/services/api';
import {isEmailValid} from '../../common/helpers/is-email-valid.helper';
import FileDownloadMixin from '@/mixins/file-download';

const nipRegex = /^[0-9]{10}$/;

export default {
	name: 'SubmitConfiguration',
	mixins: [FileDownloadMixin],
	props: {
		isPartnerArea: {
			type: Boolean,
			default: false,
		},
		isDownloadEnabled: {
			type: Boolean,
			default: false,
		},
		companyName: {
			type: String,
		},
		address: {
			type: String,
		},
		nip: {
			type: String,
		},
	},
	data() {
		return {
			name: '',
			message: '',
			email: '',
			status: 'idle',
			phone: '',
			consent1: false,
			consent2: false,
			consent3: false,
			didTryToSubmit: false,
		};
	},
	computed: {
		isEmailValid() {
			return isEmailValid(this.email);
		},
		isPhoneValid() {
			return this.isPartnerArea || this.phone.length >= 6;
		},
		isNameValid() {
			return this.isPartnerArea || this.name;
		},
		isAbleToSend() {
			if (this.status.startsWith('pending')) {
				return false;
			}
			if (!this.isPartnerArea) {
				return true;
			}
			if (!this.isEmailValid) {
				return false;
			}
			return this.companyName && this.address && nipRegex.test(this.nip);
		},
		isAbleToDownload() {
			if (this.status.startsWith('pending')) {
				return false;
			}
			return this.isPartnerArea
				? this.companyName && this.address && nipRegex.test(this.nip)
				: this.consent1 && this.consent2 && this.consent3;
		},
		user() {
			return this.$store.state.user;
		},
	},
	methods: {
		async submit() {
			if (!this.isPartnerArea) {
				this.didTryToSubmit = true;
				if (!this.isEmailValid || !this.isPhoneValid || !this.isNameValid) {
					return;
				}
				if (!this.consent1 || !this.consent2 || !this.consent3) {
					return;
				}
			}

			this.status = 'pending-send';
			const payload = this.getPayload();

			try {
				const unitConfigurationId = this.getUnitConfigurationId();
				if (unitConfigurationId) {
					await api.patch(`/sendConfiguration/${unitConfigurationId}`, payload);
				} else {
					await api.post('/sendConfiguration', payload);
				}
				this.status = 'success';
				this.message = 'Pomyślnie wysłano konfigurację.';
			} catch (e) {
				this.status = 'error';
				this.message = 'Wystąpił nieoczekiwany błąd przy próbie wysyłania konfiguracji';
			}
		},
		getPayload() {
			const unit = this.$store.getters['unitConfiguration/data']('unit');
			const baseUnit = [
				unit.fullName,
				formatPrice(this.$store.getters['unitConfiguration/baseCost']),
			];

			const groupPrices = (acc, [_, price]) => acc.concat(price);

			const componentCountRegex = /^(\d+)\s*x\s*/;

			const components = R.pipe(
				R.reduceBy(groupPrices, [], R.head),
				R.toPairs,
				R.map(([name, prices]) => [
					prices.length > 1 ? `${prices.length} x ${name}` : name,
					R.pipe(R.sum, formatPrice)(prices),
				]),
				R.map(([name, price]) => {
					const count = R.propOr('1', 1, componentCountRegex.exec(name));
					return [name.replace(componentCountRegex, ''), price, count];
				})
			)(this.$store.getters['unitConfiguration/extras']);

			const price = this.$store.getters['unitConfiguration/totalCostWithDiscountIncluded'];
			const unitConfigurationDump = this.$store.state.unitConfiguration;

			const payload = {
				baseUnit,
				components,
				email: this.email,
				phone: this.phone,
				name: this.name,
				price,
				unitConfigurationDump,
				remark: this.$store.state.app.remark,
			};

			if (this.isPartnerArea) {
				payload.companyName = this.companyName;
				payload.address = this.address;
				payload.nip = this.nip;
			}
			return payload;
		},
		getUnitConfigurationId() {
			return this.$route.query.unitConfigurationId || this.$store.state.app.unitConfigurationId;
		},
		async submitDownload() {
			this.status = 'pending-download';
			const payload = this.getPayload();
			const configurationId = this.getUnitConfigurationId();
			let result;
			try {
				if (configurationId) {
					({result} = (await api.patch(`/downloadConfiguration/${configurationId}`, payload)).data);
				} else {
					({result} = (await api.post('/downloadConfiguration', payload)).data);
				}
				this.forceFileDownloadFromUrl(result.path);
				this.status = 'success';
				this.message = 'Pomyślnie utworzono ofertę.';
				this.$store.commit('M_SET_APP_STATE', {
					unitConfigurationId: result.id,
				});
			} catch (e) {
				this.status = 'error';
				this.message = 'Wystąpił nieoczekiwany błąd przy próbie tworzenia oferty.';
			}
		},
	},
};
</script>
<style lang="scss" scoped>
.submit-configuration {
	$root: &;

	min-height: 10rem;
	& > div {
		display: flex;
		flex-direction: column;
	}

	& > div > span {
		display: inline-block;
		margin-left: 10px;
	}

	&__success {
		color: #38801c;
	}

	&__error {
		color: #801c1c;
	}

	&__header {
		margin: 0 0 2rem 10px;
		padding: 0;
	}

	input {
		border: 1px solid #e5e5e3;
		padding: 0.65rem 1.25rem;
		border-radius: 5px;
		margin-top: 0;
		margin-left: 10px;
		width: 100%;
	}

	button {
		margin-top: 10px;
	}

	&--partner-area {
		margin-top: 2rem;

		span {
			margin-left: 0;
		}

		input {
			margin-left: 0;
			margin-right: 0;
			border: 1px solid #e5e5e3;
		}

		button {
			margin-left: 0;
		}
	}

	&--client-area {
		max-width: 450px;

		& > div {
			padding: 2.25rem 2.25rem 2.25rem 1.5rem;
			background-color: #f4f4f4;
		}

		input {
			padding-top: 1.15rem;
			padding-bottom: 1.15rem;
		}

		#{$root}__input {
			padding-right: 10px;

			span {
				line-height: 1rem;
				min-height: 1rem;
				font-size: 10px;
				display: block;
				color: #ec2c2c;
				margin-left: 2rem;
			}

			&--error {
				input {
					border: 1px solid #ec2c2c;
				}
			}
		}
	}

	@media (max-width: 1100px) {
		input {
			margin-left: 0;
			margin-right: 0;
		}
		input:first-child {
			margin-top: 25px;
		}

		button {
			max-width: none;
		}
	}
}
</style>
